import { SpecFieldNames } from '../../specification/constants/specification-field-names.enum';
import { SpecificationFormEnum } from '../../specification/models/specification-form.enum';
import { OrderCreationData } from '../models/order-creation-data';
import { SpecificationFormState } from '../../specification/reducers/specification/specification.reducer';
import { IProduct } from '@cemex-core/cmx-api/models/product/models/product.interface';
import { IJobsiteState } from '../../location/reducers/jobsite-reducer/jobsite.reducer';
import { createSelector } from '@ngrx/store';
import { IShipmentLocation } from '@cemex-core/cmx-api/models/shipment-location/shipment-location';
import { IContact } from '@cemex-core/cmx-api/models/contact/contact';
import { IAddress } from '@cemex-core/cmx-api/models/address/address.interface';
import { catalogToFieldConversion, fieldToCatalogConversion } from '../models/catalog-form-field-convertions';
import {
  getDefaultPaymentTerm,
  getJobsiteState,
  getPointOfDeliveryState,
  getPurchaseOrderState,
  getSelectedBusinessLine,
  getSelectedShippingConditions,
  getSpecialInstructionsState,
  getSpecificationForm,
  getHaulerState,
  getAnticipatedInvoiceState,
  getMasterContractState,
  getSpecificationFormProductsRaw,
  getExperiencesState,
} from '../../../reducers';
import { getReceivingPersonState } from '@ordersnproducts/modules/shared/modules/receiving-person/redux/selectors/index';
import { OrderProfileKeys } from '../../../../shared/models/order-profile-keys.enum';
import { IHauler } from '@cemex-core/cmx-api/models/hauler';
import { ISalesDocument } from '@cemex-core/cmx-api/models/sales-document-management';
import { getLegalEntity } from '@cemex/cmx-client-layout';
import { IRequestedAddressResponse, ProductOP } from '@cemex-core/cmx-api';
import { _safe } from '@ordersnproducts/modules/shared/utils/safe-get.method';

export const getSpecificationFormValuesEntities = createSelector(
  getSpecificationForm,
  (state: SpecificationFormState) => {
    const entities = {};
    state.forms
      .filter(form => form.type === SpecificationFormEnum.PRODUCT_FORM)
      .forEach(value => {
        entities[value.id] = value.values;
      });

    return entities;
  },
);
export const getPumpingFormsValuesEntities = createSelector(getSpecificationForm, (state: SpecificationFormState) => {
  const entities = {};
  state.forms
    .filter(form => form.type === SpecificationFormEnum.PUMPING_FORM)
    .forEach(value => {
      entities[value.id] = { values: value.values, relatedProductId: value.relatedProductId };
    });

  return entities;
});
export const getJobsiteAddress = createSelector(getJobsiteState, (state): IAddress => state.address);
export const getJobsiteSelected = createSelector(getJobsiteState, (state): IShipmentLocation => state.selected);
export const getPODSelected = createSelector(getPointOfDeliveryState, (state): IShipmentLocation => state.selected);
export const getPODAddress = createSelector(
  getPointOfDeliveryState,
  (state): IAddress | IRequestedAddressResponse => state.address,
);
export const getReceivingPersonSelected = createSelector(getReceivingPersonState, (state): IContact => state.selected);
export const getPurchaseOrderSelected = createSelector(getPurchaseOrderState, state => state.selected);
export const getSpecialInstructionsSelected = createSelector(getSpecialInstructionsState, state => state.selected);
export const getSalesAreas = createSelector(getJobsiteState, (state: IJobsiteState) => state.jobsiteSalesArea);
export const getCatalogs = createSelector(getSpecificationForm, (state: SpecificationFormState) => state.catalogs);
export const getHauler = createSelector(getHaulerState, (state): IHauler => state.selected);
export const getAnticipatedInvoice = createSelector(
  getAnticipatedInvoiceState,
  (state): ISalesDocument => state.selected,
);

/**
 * Jobsite + Point of delivery selectors
 */
export const getShimpmentLocations = createSelector(
  getJobsiteSelected,
  getPODSelected,
  (jobsite: IShipmentLocation, pointOfDelivery: IShipmentLocation) => {
    return {
      jobsite,
      pointOfDelivery,
    };
  },
);

/**
 * Jobsite + Point of delivery + Experience(For VL validations) selectors
 */
export const getShimpmentLocationsAndExperience = createSelector(
  getJobsiteSelected,
  getPODSelected,
  getExperiencesState,
  (jobsite: IShipmentLocation, pointOfDelivery: IShipmentLocation, experience) => {
    return {
      jobsite,
      pointOfDelivery,
      experience,
    };
  },
);

export const getShipmentLocationsWithAddress = createSelector(
  getShimpmentLocations,
  getJobsiteAddress,
  getPODAddress,
  (
    shipmentLocations: { jobsite: IShipmentLocation; pointOfDelivery: IShipmentLocation },
    jobsiteAddress: IAddress,
    pointOfDeliveryAddress: IAddress,
  ) => {
    return {
      jobsite: shipmentLocations.jobsite,
      pointOfDelivery: shipmentLocations.pointOfDelivery,
      jobsiteAddress,
      pointOfDeliveryAddress,
    };
  },
);

export const getLocationSelectedStates = createSelector(
  getShimpmentLocations,
  getReceivingPersonSelected,
  getPurchaseOrderSelected,
  getSpecialInstructionsSelected,
  getJobsiteAddress,
  getPODAddress,
  getHauler,
  getAnticipatedInvoice,
  (
    shipmentLocations: { jobsite: IShipmentLocation; pointOfDelivery: IShipmentLocation },
    recievingPerson: IContact,
    purchaseOrder: string,
    specialInstructions: string,
    jobsiteAddress: IAddress,
    pointOfDeliveryAddress: IAddress,
    hauler: IHauler,
    anticipatedInvoice: ISalesDocument,
  ) => {
    return {
      jobsiteAddress,
      jobsite: shipmentLocations.jobsite,
      pointOfDelivery: shipmentLocations.pointOfDelivery,
      pointOfDeliveryAddress,
      recievingPerson,
      purchaseOrder,
      specialInstructions,
      hauler,
      anticipatedInvoice,
    };
  },
);

export const getSelectedProducts = createSelector(
  getSpecificationForm,
  getSpecificationFormValuesEntities,
  (specForm: SpecificationFormState, specificationForms) => {
    const selectedProductIds = [];
    for (const key of Object.keys(specificationForms)) {
      const val = specificationForms[key];
      const productId = _safe(() => (val[SpecFieldNames.PRODUCT_SELECTION] as IProduct).product.productId,
        (val[SpecFieldNames.PRODUCT_SELECTION] as ProductOP).productId);
      selectedProductIds.push(Number(productId));
    }

    return specForm.productSelectionProducts.filter(product => {
      const productId = _safe(() => product.product.productId, product.productId);
      return selectedProductIds.includes(Number(productId));
    });
  },
);

export const getSelectedProductsEntities = createSelector(getSelectedProducts, (products: IProduct[]) => {
  return products.reduce((entities, product) => {
    const prodId = _safe(() => product.product.productId, product.productId);
    return Object.assign({}, entities, {
      [prodId]: product,
    });
  }, {});
});

export const getCatalogsEntities = createSelector(getCatalogs, catalogs => {
  return catalogs.reduce((entities, catalog) => {
    return Object.assign({}, entities, {
      [catalog.catalogCode]: catalog,
    });
  }, {});
});

export const getBusinessSelectedStates = createSelector(
  getSelectedBusinessLine,
  getSelectedShippingConditions,
  (businessLine, shippingCondition) => {
    return {
      businessLine,
      shippingCondition,
    };
  },
);

export const getSpecificationSelectedStates = createSelector(
  getSpecificationFormValuesEntities,
  getSelectedProductsEntities,
  getCatalogsEntities,
  getPumpingFormsValuesEntities,
  (specificationForms, products, catalogs, pumpingForm) => {
    return {
      specificationForms,
      products,
      catalogs,
      pumpingForm,
    };
  },
);

export const getReviewState = createSelector(
  getSpecificationSelectedStates,
  getLocationSelectedStates,
  getBusinessSelectedStates,
  getLegalEntity,
  getSalesAreas,
  getDefaultPaymentTerm,
  getMasterContractState,
  getSpecificationFormProductsRaw,
  (
    specificationStates,
    location,
    businessLineStates,
    customer,
    salesAreas,
    defaultPaymentTerm,
    masterContractState,
    productsRaw,
  ): OrderCreationData => {
    return {
      ...specificationStates,
      ...location,
      ...businessLineStates,
      customer,
      salesAreas,
      defaultPaymentTerm,
      masterContract: masterContractState.selectedContract as any,
      productsRaw,
    } as OrderCreationData;
  },
);

export const getFormCatalogKeys = createSelector(getReviewState, (state: OrderCreationData) => {
  return Object.keys(fieldToCatalogConversion).filter(fieldKey => {
    return Object.keys(state.specificationForms).includes(fieldKey);
  });
});

export const getAvailableCatalogs = createSelector(getReviewState, (state: OrderCreationData) => {
  const allCatalogsKeys = Object.values(catalogToFieldConversion) as string[];
  const formCatalogEntries = Object.keys(state.specificationForms).map(specFormId => {
    const specForm = state.specificationForms[specFormId];
    const formattedCatalog = Object.keys(specForm)
      .filter(field => {
        return allCatalogsKeys.includes(field);
      })
      .map(specFormKey => {
        return state.catalogs[fieldToCatalogConversion[specFormKey]].entries.find(entry => {
          return entry.entryCode === specForm[specFormKey];
        });
      })
      .filter(Boolean);

    return {
      formId: specFormId,
      catalogs: formattedCatalog,
    };
  });

  // convert to object
  const result = {};
  formCatalogEntries.map(entry => {
    Object.assign(result, {
      [entry.formId]: entry.catalogs,
    });
  });

  return result;
});

export const getAvailableOrderProfileKeys = createSelector(
  getSpecificationForm,
  (specsFormState: SpecificationFormState) => {
    const formKeys = Object.keys(specsFormState.forms[0].values);
    return Object.keys(OrderProfileKeys)
      .map(key => OrderProfileKeys[key])
      .filter(key => {
        return formKeys.includes(key);
      });
  },
);
